import * as React from "react";
import { AlertColor, Box, Grid } from "@mui/material";
import CclInfoTable, {
    CclInfoTableRowTemplate,
} from "../../../../components/common/cclInfoTable/cclInfoTable";
import { CclInfoTblRegistrationGeneralTemplate } from "./cclInfoTblRegistrationGeneralTemplate";
import {
    Participant,
    SetParticipantExpirationPayload,
    SetParticipantStatusPayload,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import {
    useSetParticipantExpirationDateMutation,
    useSetParticipantStatusMutation,
} from "../../../../services/cclTokenedEnterpriseParticipantApi";
import { eInfoTableValueFieldEditType } from "../../../../components/common/cclInfoTable/cclInfoTableValueField";
import { AccessEventRegistrationDetails } from "../../../../services/types/accessEventTypes";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import ParticipantAssignedResourcesDataGrid from "./participantAssignedResourcesDataGrid";
import { CclInfoTblRegistrationProductSaleTemplate } from "./cclInfoTbiRegistrationProductSaleTemplate";
import { CclInfoTblRegistrationSessionTemplate } from "./cclInfoTblRegistrationSessionTemplate";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../../services/currentUserService/currentUserService";
import EditUserDrawer from "../../../../components/shared/editUserDrawer/editUserDrawer";

interface RegistrationGeneralPanelProps {
    registration: Participant | undefined;
    isNwaSession: boolean;
    isClientAdmin?: boolean;
}

const RegistrationGeneralPanel: React.FC<RegistrationGeneralPanelProps> = (props) => {
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [generalRegistrationTemplate, setGeneralRegistrationTemplate] = React.useState<
        CclInfoTableRowTemplate[]
    >(
        props.registration?.sessionTypeKey === 7
            ? CclInfoTblRegistrationProductSaleTemplate
            : CclInfoTblRegistrationGeneralTemplate,
    );
    const [generalSessionTemplate, setGeneralSessionTemplate] = React.useState<
        CclInfoTableRowTemplate[]
    >(CclInfoTblRegistrationSessionTemplate);
    const [registration, setRegistration] = React.useState<Participant | undefined>(
        props.registration,
    );
    const [editDrawerOpen, setEditDrawerOpen] = React.useState<boolean>(false);

    const [saveExpiration, { isLoading: expirationIsLoading }] =
        useSetParticipantExpirationDateMutation();
    const [saveStatus, { isLoading: statusIsLoading }] = useSetParticipantStatusMutation();
    const { logEvent } = useLogAccessEvent();
    const claimsService = new UserClaimsService();

    const isProductSale = props.registration?.sessionTypeKey === 7;
    const isPartnerSession = props.isNwaSession;
    const canEditProductSale = claimsService.IsUserInGroups([
        eGroupIds.eCCLSuperAdmin,
        eGroupIds.eCCLAdmin,
        eGroupIds.eClientAdmin,
    ]);
    const canEditPartnerSession = claimsService.IsUserInGroups([
        eGroupIds.eCCLSuperAdmin,
        eGroupIds.eCCLAdmin,
        eGroupIds.eNWAAdmin,
    ]);

    const showEditButton =
        (isProductSale && canEditProductSale) || (isPartnerSession && canEditPartnerSession);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const saveParticipantDetails = (e: any) => {
        for (let i = 0; i < e.length; i++) {
            if (e[i].name === "expirationDate") {
                if (props.registration) {
                    let expirationArgs: SetParticipantExpirationPayload = {
                        esiKey: props.registration?.esiKey,
                        payload: {
                            ExpirationDate: e[i].value.toISOString(),
                        },
                    };
                    saveExpiration(expirationArgs)
                        .unwrap()
                        .then(() => {
                            const evtData: AccessEventRegistrationDetails = {
                                esiKey: props.registration?.esiKey.toString() ?? "",
                                imKey: props.registration?.imKey.toString() ?? "",
                                email: props.registration?.emailAddress ?? "",
                            };
                            logEvent("RegistrationDetailsEdited", evtData);
                            ShowSnackbarElement("Registration Updated", "success");
                        })
                        .catch(() => {
                            ShowSnackbarElement("Registration Update Failed", "error");
                        });
                }
            } else if (e[i].name === "isComplete") {
                if (props.registration) {
                    let statusArgs: SetParticipantStatusPayload = {
                        esiKey: props.registration?.esiKey,
                        payload: {
                            Completed: e[i].value,
                        },
                    };
                    saveStatus(statusArgs)
                        .unwrap()
                        .then(() => {
                            const evtData: AccessEventRegistrationDetails = {
                                esiKey: props.registration?.esiKey.toString() ?? "",
                                imKey: props.registration?.imKey.toString() ?? "",
                                email: props.registration?.emailAddress ?? "",
                            };
                            logEvent("RegistrationDetailsEdited", evtData);
                            ShowSnackbarElement("Registration Updated", "success");
                        })
                        .catch(() => {
                            ShowSnackbarElement("Registration Update Failed", "error");
                        });
                }
            }
        }
    };

    const handleEditDrawerClose = (
        success: boolean,
        newFirstName?: string,
        newLastName?: string,
    ) => {
        setEditDrawerOpen(false);
        if (success) {
            const evtData: AccessEventRegistrationDetails = {
                esiKey: props.registration?.esiKey.toString() ?? "",
                projectCode: props.registration?.projectCode ?? "",
                imKey: props.registration?.imKey.toString() ?? "",
                email: props.registration?.emailAddress ?? "",
            };
            logEvent("RegistrationDetailsEdited", evtData);
            if (newFirstName !== undefined && newLastName !== undefined) {
                setRegistration((prev) =>
                    prev
                        ? {
                              ...prev,
                              firstName: newFirstName,
                              lastName: newLastName,
                              fullName: `${newFirstName} ${newLastName}`,
                          }
                        : prev,
                );
            }
            ShowSnackbarElement("Participant Updated", "success");
        }
    };

    React.useEffect(() => {
        // a bit of a hack here modifying the template. Need to fix in the future with better info table implementation
        if (props.registration != null) {
            let pax = props.registration;
            pax.sessionStatus = pax.session?.status;
            pax.sessionTitle = pax.session?.title;
            pax.sessionCode = pax.session?.sessionCode;
            pax.sessionKey = pax.session?.sessionKey;
            setRegistration(pax);

            let regTmplt = [...generalRegistrationTemplate];
            if (props.isClientAdmin) {
                regTmplt = regTmplt.filter((r) => r.id !== "ceRegistrationId");
            }
            let row = regTmplt.find((r) => r.id === "fullName");
            if (row != null) {
                row.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
                row.valueField.url = `/user?email=${encodeURIComponent(
                    props.registration.emailAddress,
                )}&imkey=${props.registration.imKey}`;
            }

            row = regTmplt.find((r) => r.id === "imKey");
            if (row != null) {
                row.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
                row.valueField.url = `/user?email=${encodeURIComponent(
                    props.registration.emailAddress,
                )}&imkey=${props.registration.imKey}&firstName=${
                    props.registration.firstName
                }&lastName=${props.registration.lastName}`;
            }

            if (!pax.isCoachingParticipant) {
                regTmplt = regTmplt.filter(
                    (r) => r.id !== "expirationDate" && r.id !== "isComplete",
                );
            }

            setGeneralRegistrationTemplate(regTmplt);

            let sessionTmplt = [...generalSessionTemplate];
            if (props.registration?.sessionTypeKey === 7) {
                sessionTmplt = sessionTmplt.filter((r) => r.id !== "sessionCode");
            }

            row = sessionTmplt.find((r) => r.id === "sessionCode");
            if (row != null) {
                row.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
                row.valueField.url = `/session/${props.registration.session.sessionKey}`;
            }

            row = sessionTmplt.find((r) => r.id === "sessionKey");
            if (row != null) {
                row.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
                row.valueField.url = `/session/${props.registration.session.sessionKey}`;
            }
            setGeneralSessionTemplate(sessionTmplt);
        }
        // eslint-disable-next-line
    }, [props.registration]);

    const editButton = showEditButton ? (
        <Box width={1} sx={{ marginRight: 4 }}>
            <CclButton
                mode="primary"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setEditDrawerOpen(true);
                }}
                sx={{ float: "right" }}
            >
                Edit
            </CclButton>
        </Box>
    ) : undefined;

    if (registration != null) {
        return (
            <>
                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                    />
                ) : null}
                <Grid container spacing={2} height={"content"}>
                    <Grid item xs={12} sm={6}>
                        <Grid container spacing={2} height={"content"}>
                            <Grid item xs={12}>
                                <CclGenericAccordian
                                    headingText={"Participant"}
                                    sx={{ height: "100%" }}
                                    summaryRightSideElement={editButton}
                                >
                                    <Box sx={{ m: 2 }}>
                                        <CclInfoTable
                                            onSave={saveParticipantDetails}
                                            template={generalRegistrationTemplate}
                                            data={registration}
                                            saving={expirationIsLoading || statusIsLoading}
                                            readonly={props.isClientAdmin}
                                            hideSave={props.isClientAdmin}
                                        />
                                    </Box>
                                </CclGenericAccordian>
                            </Grid>
                            <Grid item xs={12}>
                                <CclGenericAccordian
                                    headingText={"Session"}
                                    sx={{ height: "100%" }}
                                >
                                    <Box sx={{ m: 2 }}>
                                        <CclInfoTable
                                            template={generalSessionTemplate}
                                            data={registration}
                                        />
                                    </Box>
                                </CclGenericAccordian>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {registration.sessionTypeKey !== 7 && (
                            <CclGenericAccordian
                                headingText={"Assigned Resources"}
                                sx={{ height: "100%" }}
                            >
                                <ParticipantAssignedResourcesDataGrid
                                    registration={registration}
                                    isNwaSession={props.isNwaSession}
                                />
                            </CclGenericAccordian>
                        )}
                    </Grid>
                </Grid>
                {showEditButton && editDrawerOpen ? (
                    <EditUserDrawer
                        title="Edit Participant Name"
                        open={true}
                        imKey={registration.imKey}
                        email={registration.emailAddress}
                        firstName={registration.firstName}
                        lastName={registration.lastName}
                        onClose={handleEditDrawerClose}
                    />
                ) : null}
            </>
        );
    }

    return <span>No Registration Data Loaded</span>;
};

export default RegistrationGeneralPanel;
